import {
  Button,
  FieldGroupIconButton,
  Flex,
  Grid,
  Loader,
  ScrollView,
  SearchField,
  SelectField,
  Text,
  TextField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";

import { useContext, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import moment from "moment";
import {
  CDropdown,
  CDropdownDivider,
  CDropdownItem,
  CDropdownMenu,
  CDropdownToggle,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";
import Tippy from "@tippyjs/react";
import {
  Alarm,
  AssignmentTurnedIn,
  Checklist,
  Close,
  Delete,
  DoDisturbOn,
  Edit,
  EditCalendar,
  EventAvailable,
  Info,
  ManageSearch,
  Pause,
  PhotoLibrary,
  PictureAsPdf,
  Visibility,
} from "@mui/icons-material";

import {
  serviceOrdersByBranchID,
  serviceOrdersByCompanyID,
  serviceOrderToolsByServiceOrderId,
  sparesByServiceOrderID,
  serviceOrderMaintenanceRoutinesByServiceOrderId,
  serviceOrderEquipmentsByServiceOrderId,
  serviceOrderLogsByCompanyID,
} from "graphql/queries";
import {
  createServiceOrderLog,
  deleteServiceOrderEquipment,
  deleteServiceOrderMaintenanceRoutine,
  deleteServiceOrderTool,
  exportServiceOrder,
  updateServiceOrder,
  updateSpare,
} from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import { ServiceOrderCreateForm, ServiceOrderUpdateForm } from ".";
import CompanyBranchFilter from "components/companies/CompanyBranchFilter";
import { LayoutContext } from "layout/context/LayoutContext";
import {
  getBranchName,
  getCompanyName,
  getUserEmail,
} from "graphql/custom-queries";
import ConfirmModal from "components/utils/ConfirmModal";
import ActionIcon from "components/actions/ActionIcon";
import LoaderFullScreen from "components/utils/LoaderFullScreen";
import ExecutedServiceOrderContainer from "./ExecutedServiceOrder/ExecutedServiceOrderContainer";
import SelectUser from "components/users/SelectUser";
import { serviceOrderStatus } from "config/constants";
import { globalStrings } from "config/translations/es";
import ServiceOrderResume from "./ServiceOrderResume";
import ServiceOrderPhotos from "./ServiceOrderPhotos";
import MultipleSelect from "components/utils/MultipleSelect";
import { useOnlineStatus } from "hooks/useOnlineStatus";
import {
  localGetAllServiceOrders,
  localGetServiceOrder,
} from "libs/idb/service-orders-db";
import ServiceOrderLogs from "./ServiceOrderLogs";
import DateRangeForm from "components/utils/DateRangeForm";
import { convertToSlug } from "components/utils/functions";

const ServiceOrders = () => {
  const { me } = useContext(LayoutContext);
  const online = useOnlineStatus();
  const [
    canCreate,
    canUpdate,
    canDelete,
    canExecute,
    canExport,
    canCancel,
    canReadPhotos,
    canReadLogs,
  ] = usePermissions([
    "service-order:create",
    "service-order:update",
    "service-order:delete",
    "service-order:execute",
    "service-order:export",
    "service-order:cancel",
    "service-order:read-photo",
    "service-order:read-log",
  ]);
  const [loading, setLoading] = useState(false);
  const [serviceOrders, setServiceOrders] = useState([]);
  const [serviceOrdersLoading, setServiceOrdersLoading] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [userSelected, setUserSelected] = useState("");
  const [paginationTokens, setPaginationTokens] = useState({
    previous: [],
    nextToken: null,
  });
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    serviceOrderId: null,
    serviceOrderCode: null,
  });
  const [modalServiceOrder, setModalServiceOrder] = useState({
    visible: false,
    serviceOrderId: null,
  });
  const [modalDateVisible, setModalDateVisible] = useState(false);
  const [searchBy, setSearchBy] = useState({
    company: {
      id: "",
      display: "",
    },
    branch: {
      id: "",
      display: "",
    },
    status: [],
    from: null,
    to: null,
  });
  const columnsMapped = useMemo(
    () =>
      serviceOrders.map((item) => ({
        ...item,
        orders: (
          <Grid>
            <span>{item.title}</span>
            <span className="text-muted" style={{ fontSize: "14px" }}>
              {item.companyName || <Loader />}
            </span>
            <span className="text-muted" style={{ fontSize: "14px" }}>
              {item.branchName || <Loader />}
            </span>
            <span className="text-muted" style={{ fontSize: "12px" }}>
              {item.start && moment(item.start).format("YYYY-MM-DD HH:mm")}
            </span>
            <span className="text-muted" style={{ fontSize: "12px" }}>
              {item.end && moment(item.end).format("YYYY-MM-DD HH:mm")}
            </span>
          </Grid>
        ),
        title: (
          <Grid>
            <span>{item.title}</span>
            <span className="text-muted" style={{ fontSize: "12px" }}>
              {item.start && moment(item.start).format("YYYY-MM-DD HH:mm")}
            </span>
            <span className="text-muted" style={{ fontSize: "12px" }}>
              {item.end && moment(item.end).format("YYYY-MM-DD HH:mm")}
            </span>
          </Grid>
        ),
        companyName: item.companyName || <Loader />,
        branchName: item.branchName || <Loader />,
        userExecutorName: item.hasOwnProperty("userExecutorName") ? (
          item.userExecutorName
        ) : (
          <Loader />
        ),
        status: (
          <Tippy
            content={`${globalStrings.serviceOrderStatus[item.status]}${
              !!item.offline ? " - 🔴Offline" : ""
            }`}
          >
            {item.status === serviceOrderStatus.scheduled ? (
              <EventAvailable color={!!item.offline ? "error" : "primary"} />
            ) : item.status === serviceOrderStatus.executed ? (
              <AssignmentTurnedIn
                color={!!item.offline ? "error" : "primary"}
              />
            ) : item.status === serviceOrderStatus.canceled ? (
              <DoDisturbOn color={!!item.offline ? "error" : "primary"} />
            ) : item.status === serviceOrderStatus.paused ? (
              <Pause color={!!item.offline ? "error" : "primary"} />
            ) : item.status === serviceOrderStatus.executing ? (
              <Alarm color={!!item.offline ? "error" : "primary"} />
            ) : (
              <Info color={!!item.offline ? "error" : "primary"} />
            )}
          </Tippy>
        ),
        actions: (
          <>
            <CDropdown variant="dropdown" style={{ textAlign: "right" }}>
              <CDropdownToggle
                placement="bottom-end"
                className="py-0 pe-0 border-0"
                caret={false}
              >
                <ActionIcon
                  name={strings.serviceOrders.moreOptions}
                  icon="MoreVert"
                  color="action"
                />
              </CDropdownToggle>
              <CDropdownMenu className="pt-0" placement="bottom-end">
                <CDropdownItem
                  role="button"
                  className="d-inline-flex align-items-center mt-1"
                  onClick={() => openModal("details", item.id)}
                >
                  <Visibility
                    fontSize="small"
                    color="action"
                    className="me-1"
                  />{" "}
                  {strings.serviceOrders.details}
                </CDropdownItem>
                {item.status !== serviceOrderStatus.executed &&
                  !item.offline &&
                  canUpdate && (
                    <CDropdownItem
                      role="button"
                      className="d-inline-flex align-items-center mt-1"
                      onClick={() => openModal("update", item.id)}
                    >
                      <Edit fontSize="small" color="action" className="me-1" />{" "}
                      {strings.serviceOrders.edit}
                    </CDropdownItem>
                  )}
                {canReadPhotos && (
                  <CDropdownItem
                    role="button"
                    className="d-inline-flex align-items-center mt-1"
                    onClick={() => openModal("read-photo", item.id)}
                  >
                    <PhotoLibrary
                      fontSize="small"
                      color="action"
                      className="me-1"
                    />{" "}
                    {strings.serviceOrders.readPhotos}
                  </CDropdownItem>
                )}
                {canReadLogs && (
                  <CDropdownItem
                    role="button"
                    className="d-inline-flex align-items-center mt-1"
                    onClick={() => openModal("read-log", item.id)}
                  >
                    <ManageSearch
                      fontSize="small"
                      color="action"
                      className="me-1"
                    />{" "}
                    {strings.serviceOrders.readLogs}
                  </CDropdownItem>
                )}
                {((item.offline && item.isLocal) || !item.offline) &&
                  item.status !== serviceOrderStatus.executed &&
                  canExecute && (
                    <CDropdownItem
                      role="button"
                      className="d-inline-flex align-items-center mt-1"
                      onClick={() =>
                        openModal("execute", item.id, item.offline)
                      }
                    >
                      <Checklist
                        fontSize="small"
                        color="action"
                        className="me-1"
                      />{" "}
                      {strings.serviceOrders.execute}
                    </CDropdownItem>
                  )}
                {canExport && (
                  <CDropdownItem
                    role="button"
                    className="d-inline-flex align-items-center mt-1"
                    onClick={() => serviceOrderExport(item.id)}
                  >
                    <PictureAsPdf
                      fontSize="small"
                      color="action"
                      className="me-1"
                    />{" "}
                    {strings.serviceOrders.export}
                  </CDropdownItem>
                )}
                {item.status !== serviceOrderStatus.canceled && canCancel && (
                  <CDropdownItem
                    role="button"
                    className="d-inline-flex align-items-center mt-1"
                    onClick={() => {
                      setModalDetails({
                        visible: true,
                        serviceOrderId: item.id,
                        serviceOrderCode: item.code,
                        type: "cancel",
                      });
                    }}
                  >
                    <DoDisturbOn
                      fontSize="small"
                      color="action"
                      className="me-1"
                    />{" "}
                    {strings.serviceOrders.cancel}
                  </CDropdownItem>
                )}
                {canDelete && !item.offline && (
                  <>
                    <CDropdownDivider />
                    <CDropdownItem
                      role="button"
                      className="d-inline-flex align-items-center"
                      onClick={() => {
                        setModalDetails({
                          visible: true,
                          serviceOrderId: item.id,
                          serviceOrderCode: item.code,
                        });
                      }}
                    >
                      <Delete fontSize="small" color="error" className="me-1" />{" "}
                      <Text variation="error">
                        {strings.serviceOrders.delete}
                      </Text>
                    </CDropdownItem>
                  </>
                )}
              </CDropdownMenu>
            </CDropdown>
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [serviceOrders]
  );

  const onChangeStatus = (value) => {
    setSearchBy((prev) => ({ ...prev, status: value }));
  };

  const onNext = () => {
    fetchServiceOrders(paginationTokens.nextToken);
  };
  const onPrevious = () => {
    const previous =
      paginationTokens.previous?.length <= 1
        ? null
        : paginationTokens.previous?.slice(-2)[0];
    fetchServiceOrders(previous, false);
  };
  const onKeywordSubmit = (keywordValue) => {
    const keywordSplit = keywordValue && keywordValue.split(" ");
    setPaginationTokens({
      previous: [],
      nextToken: [],
    });
    setKeyword(keywordSplit);
  };
  const openModal = (action, id, offline) =>
    setModalServiceOrder({
      visible: true,
      action,
      serviceOrderId: id,
      offline,
    });
  const onSuccessServiceOrder = () => {
    setModalServiceOrder({ visible: false });
    fetchServiceOrders();
  };
  const onSelectUser = (user) => {
    setPaginationTokens({
      previous: [],
      nextToken: [],
    });
    setUserSelected(user);
  };

  const onSubmitDateRange = (date, options) => {
    const { from, to } = date;
    setSearchBy((prev) => ({ ...prev, ...options, from, to }));
    setModalDateVisible(false);
  };

  const onClearDateRange = () => {
    setSearchBy((prev) => ({
      ...prev,
      from: null,
      to: null,
      dateRangeStatus: null,
    }));
  };

  async function fetchCompanyName(id) {
    try {
      const companyData = await API.graphql(
        graphqlOperation(getCompanyName, {
          id,
        })
      );
      const companyItem = companyData.data.getCompany;
      return companyItem?.name ?? "";
    } catch (err) {
      console.log("Error fetchCompanyName", err);
      return "";
    }
  }

  async function fetchBranchName(id) {
    try {
      const branchData = await API.graphql(
        graphqlOperation(getBranchName, {
          id,
        })
      );
      const branchItem = branchData.data.getBranch;
      return branchItem?.name ?? "";
    } catch (err) {
      console.log("Error fetchBranchName", err);
      return "";
    }
  }

  async function fetchUserEmail(id) {
    try {
      const userData = await API.graphql(
        graphqlOperation(getUserEmail, {
          id,
        })
      );
      const userItem = userData.data.getUser;
      return userItem?.email ?? "";
    } catch (err) {
      console.log("Error fetchUserEmail", err);
      return "";
    }
  }

  const getNamesByServiceOrders = (list) => {
    list?.forEach(async (item) => {
      const companyName = await fetchCompanyName(item.companyID);
      const branchName = await fetchBranchName(item.branchID);
      const userExecutorName = item.userExecutor
        ? await fetchUserEmail(item.userExecutor)
        : "";
      const isLocal = await localGetServiceOrder(item.id);

      setServiceOrders((prevList) => {
        const updatedList = [...prevList];
        const index = updatedList.findIndex(
          (itemInd) => itemInd.id === item.id
        );

        updatedList[index] = {
          ...updatedList[index],
          companyName,
          branchName,
          userExecutorName,
          isLocal,
        };

        return updatedList;
      });
    });
  };

  const fetchLocalServiceOrders = async () => {
    const data = await localGetAllServiceOrders();
    if (data && data.length > 0) {
      setServiceOrders(data.map((localData) => localData.serviceOrder));
    }
  };

  async function fetchServiceOrders(token, isNext = true, limit = 20) {
    if (!online) {
      fetchLocalServiceOrders();
      return;
    }
    let userExecutor = userSelected;
    let objOperation = {};
    let nameOperation = null;
    let nameOperationStr = "";
    try {
      setServiceOrdersLoading(true);

      if (!searchBy.company.id && !me.companyID) return;

      if (!!searchBy.branch.id) {
        objOperation = {
          branchID: searchBy.branch.id,
          sortDirection: "DESC",
        };
        nameOperation = serviceOrdersByBranchID;
        nameOperationStr = "serviceOrdersByBranchID";
      } else if (!!searchBy.company.id || !!me.companyID) {
        objOperation = {
          companyID: searchBy.company.id || me.companyID,
          sortDirection: "DESC",
        };
        nameOperation = serviceOrdersByCompanyID;
        nameOperationStr = "serviceOrdersByCompanyID";
      }

      if (me.isTechnician) userExecutor = me.id;

      let accumulated = [];
      let nextTokenTemp = token ?? null;

      const MAX_PAGE_SIZE = 20;

      do {
        const remaining = Math.max(limit - accumulated.length, 0);
        if (remaining <= 0) break;

        const pageLimit = Math.min(remaining, MAX_PAGE_SIZE);
        const objFilter = {
          ...(userExecutor ? { userExecutor: { eq: userExecutor } } : {}),
          ...(searchBy.status?.length > 0
            ? {
                or: searchBy.status.map((st) => ({
                  status: { eq: st },
                })),
              }
            : {}),
          deletedAt: { attributeExists: false },
        };

        if (searchBy.from && searchBy.to) {
          const serviceOrderLogs = await API.graphql(
            graphqlOperation(serviceOrderLogsByCompanyID, {
              companyID: searchBy.company.id || me.companyID,
              filter: {
                createdAt: {
                  between: [
                    searchBy.from.toISOString(),
                    searchBy.to.toISOString(),
                  ],
                },
                status: { eq: searchBy.dateRangeStatus },
              },
            })
          );
          let serviceOrderLogsItems =
            serviceOrderLogs.data.serviceOrderLogsByCompanyID.items;

          const idsExecutedInDateRange = [
            ...new Set(serviceOrderLogsItems.map((log) => log.serviceOrderID)),
          ];

          if (idsExecutedInDateRange.length > 0) {
            objFilter.or = [
              ...(objFilter.or || []),
              ...idsExecutedInDateRange.map((id) => ({
                id: { eq: id },
              })),
            ];
          } else {
            setServiceOrders([]);
            setPaginationTokens({ previous: [], nextToken: null });
            setServiceOrdersLoading(false);
            return;
          }
        }

        const serviceOrdersData = await API.graphql(
          graphqlOperation(nameOperation, {
            ...objOperation,
            limit: pageLimit,
            nextToken: nextTokenTemp,
            filter: keyword
              ? {
                  and: keyword.map((key) => ({
                    or: [
                      {
                        code: { contains: key },
                      },
                      {
                        description: { contains: key },
                      },
                      {
                        title: { contains: key },
                      },
                      { slug: { contains: convertToSlug(key) } },
                    ],
                  })),
                  ...objFilter,
                }
              : objFilter,
          })
        );
        const pageItems =
          serviceOrdersData?.data?.[nameOperationStr]?.items ?? [];
        const responseNextToken =
          serviceOrdersData?.data?.[nameOperationStr]?.nextToken ?? null;

        accumulated = [...accumulated, ...pageItems];

        nextTokenTemp = responseNextToken;
      } while (nextTokenTemp && accumulated.length < limit);

      setServiceOrders(accumulated);
      getNamesByServiceOrders(accumulated);
      setPaginationTokens((prevSt) => {
        let previous = prevSt.previous ?? [];
        if (isNext) previous = token ? [...previous, token] : [...previous];
        if (!isNext && previous?.length) previous.pop();

        return {
          previous,
          nextToken: nextTokenTemp,
        };
      });
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setServiceOrdersLoading(false);
    } finally {
      setServiceOrdersLoading(false);
    }
  }

  async function serviceOrderRoutinesDelete(serviceOrderId) {
    try {
      const results = await API.graphql(
        graphqlOperation(serviceOrderMaintenanceRoutinesByServiceOrderId, {
          serviceOrderId,
          filter: { deletedAt: { attributeExists: false } },
        })
      );
      const resultsToDelete =
        results.data.serviceOrderMaintenanceRoutinesByServiceOrderId.items;
      await Promise.all(
        resultsToDelete?.map((res) =>
          API.graphql(
            graphqlOperation(deleteServiceOrderMaintenanceRoutine, {
              input: { id: res.id },
            })
          )
        )
      );
    } catch (err) {
      console.log("Error al eliminar relaciones con rutinas", err);
    }
  }

  async function serviceOrderToolsDelete(serviceOrderId) {
    try {
      const results = await API.graphql(
        graphqlOperation(serviceOrderToolsByServiceOrderId, {
          serviceOrderId,
          filter: { deletedAt: { attributeExists: false } },
        })
      );
      const resultsToDelete =
        results.data.serviceOrderToolsByServiceOrderId.items;
      await Promise.all(
        resultsToDelete?.map((res) =>
          API.graphql(
            graphqlOperation(deleteServiceOrderTool, {
              input: { id: res.id },
            })
          )
        )
      );
    } catch (err) {
      console.log("Error al eliminar relaciones con herramientas", err);
    }
  }

  async function serviceOrderEquipmentsDelete(serviceOrderId) {
    try {
      const results = await API.graphql(
        graphqlOperation(serviceOrderEquipmentsByServiceOrderId, {
          serviceOrderId,
          filter: { deletedAt: { attributeExists: false } },
        })
      );
      const resultsToDelete =
        results.data.serviceOrderEquipmentsByServiceOrderId.items;
      await Promise.all(
        resultsToDelete?.map((res) =>
          API.graphql(
            graphqlOperation(deleteServiceOrderEquipment, {
              input: { id: res.id },
            })
          )
        )
      );
    } catch (err) {
      console.log("Error al eliminar relaciones con equipos", err);
    }
  }

  async function serviceOrderSparesDelete(serviceOrderID) {
    try {
      const results = await API.graphql(
        graphqlOperation(sparesByServiceOrderID, {
          serviceOrderID,
          filter: { deletedAt: { attributeExists: false } },
        })
      );
      const resultsToDelete = results.data.sparesByServiceOrderID.items;
      await Promise.all(
        resultsToDelete?.map((res) =>
          API.graphql(
            graphqlOperation(updateSpare, {
              input: { id: res.id, serviceOrderID: null },
            })
          )
        )
      );
    } catch (err) {
      console.log("Error al eliminar relaciones con repuestos", err);
    }
  }

  async function serviceOrderDelete(id) {
    try {
      setLoading(true);

      await serviceOrderRoutinesDelete(id);
      await serviceOrderToolsDelete(id);
      await serviceOrderSparesDelete(id);
      await serviceOrderEquipmentsDelete(id);

      const result = await API.graphql(
        graphqlOperation(updateServiceOrder, {
          input: { id, deletedAt: new Date().toISOString() },
        })
      );
      toast.success(strings.successDeleted);
      const deletedId = result.data.updateServiceOrder.id;
      setServiceOrders(serviceOrders.filter((item) => item.id !== deletedId));
      setModalDetails({
        visible: false,
        serviceOrderId: null,
      });
    } catch (err) {
      toast.error(strings.responses[err]);
    } finally {
      setLoading(false);
    }
  }

  async function serviceOrderCancel(id) {
    try {
      setLoading(true);

      await API.graphql(
        graphqlOperation(updateServiceOrder, {
          input: { id, status: serviceOrderStatus.canceled },
        })
      );
      await API.graphql({
        query: createServiceOrderLog.replaceAll("__typename", ""),
        variables: {
          input: {
            serviceOrderID: id,
            status: serviceOrderStatus.canceled,
            userID: me.id,
          },
        },
      });
      toast.success(strings.successCanceled);
      setServiceOrders(
        serviceOrders.map((serv) =>
          serv.id === id
            ? { ...serv, status: serviceOrderStatus.canceled }
            : serv
        )
      );
      setModalDetails({
        visible: false,
        serviceOrderId: null,
      });
    } catch (err) {
      toast.error(strings.responses[err]);
    } finally {
      setLoading(false);
    }
  }

  const serviceOrderExport = async (id) => {
    try {
      setLoading(true);

      const res = await API.graphql(
        graphqlOperation(exportServiceOrder, {
          input: { id },
        })
      );

      if (res.data.exportServiceOrder?.success) {
        toast.success(strings.successExported);

        const link = document.createElement("a");
        link.href = res.data.exportServiceOrder?.url;
        link.target = "_blank";
        link.click();
      } else {
        toast.error(
          res.data.exportServiceOrder?.error ||
            strings.responses.unexpectedError
        );
      }
    } catch (err) {
      console.log("Error export service order:", err);
      toast.error(strings.responses.unexpectedError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServiceOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchBy.company.id,
    searchBy.branch.id,
    searchBy.status,
    searchBy.from,
    online,
    keyword,
    userSelected,
  ]);

  const tableColumns = [
    {
      key: "status",
      label: "",
      _style: { width: "5%" },
    },
    ...(window.innerWidth > 768
      ? [
          {
            key: "companyName",
            label: strings.table.headers.companyName,
            _style: { width: "20%" },
          },
        ]
      : []),
    ...(window.innerWidth > 768
      ? [
          {
            key: "branchName",
            label: strings.table.headers.branchName,
            _style: { width: "20%" },
          },
        ]
      : []),
    ...(window.innerWidth > 768
      ? [
          {
            key: "userExecutorName",
            label: strings.table.headers.leaderExecutor,
            _style: { width: "15%" },
          },
        ]
      : []),
    ...(window.innerWidth > 768
      ? [
          {
            key: "title",
            label: strings.table.headers.name,
            _style: { width: "20%" },
          },
        ]
      : []),
    ...(window.innerWidth <= 768
      ? [
          {
            key: "orders",
            label: strings.table.headers.orders,
            _style: { width: "20%" },
          },
        ]
      : []),
    {
      key: "actions",
      label: "",
      _style: { width: "5%" },
    },
  ];

  return (
    <div>
      <Flex justifyContent="space-between">
        <span className="fs-3">{strings.title}</span>
        {canCreate && (
          <Button
            style={styles.buttonAdd}
            children={strings.serviceOrders.addServiceOrder}
            variation="primary"
            onClick={() => openModal("create")}
          />
        )}
      </Flex>
      <div className="p-3">
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <CompanyBranchFilter searchBy={searchBy} setSearchBy={setSearchBy} />
          <SearchField
            label={strings.table.search.label}
            placeholder={strings.table.search.placeholder}
            onSubmit={onKeywordSubmit}
            onClear={onKeywordSubmit}
          />
          {!me.isTechnician && (
            <SelectUser
              placeholder={strings.headers.searchTechnician}
              onSelect={onSelectUser}
            />
          )}
          <MultipleSelect
            options={Object.entries(globalStrings.serviceOrderStatus).map(
              (opt) => ({ label: opt[1], value: opt[0] })
            )}
            values={searchBy.status}
            onChange={onChangeStatus}
            placeholder={strings.table.search.byState}
          />
          <div style={{ marginTop: "-8px" }}>
            <TextField
              readOnly
              value={
                searchBy.from
                  ? `${moment(searchBy.from).format(
                      "YYYY-MM-DD HH:mm"
                    )} - ${moment(searchBy.to).format("YYYY-MM-DD HH:mm")} (${
                      globalStrings.serviceOrderStatus[searchBy.dateRangeStatus]
                    })`
                  : ""
              }
              onClick={() => setModalDateVisible(true)}
              placeholder={strings.searchByDateRange}
              innerEndComponent={
                searchBy.from && (
                  <FieldGroupIconButton
                    variation="link"
                    onClick={onClearDateRange}
                  >
                    <Close fontSize="16px" />
                  </FieldGroupIconButton>
                )
              }
              outerEndComponent={
                <FieldGroupIconButton onClick={() => setModalDateVisible(true)}>
                  <EditCalendar fontSize="small" />
                </FieldGroupIconButton>
              }
            />
          </div>
        </Grid>
        {serviceOrdersLoading ? (
          <Loader />
        ) : (
          <>
            <CTable
              className="table-centered"
              items={columnsMapped}
              columns={tableColumns}
              tableHeadProps={{ color: "light" }}
              // responsive
              hover
            />
            <Button
              size="small"
              disabled={paginationTokens.previous?.length <= 0}
              style={styles.buttonPrevious}
              onClick={onPrevious}
            >
              <FiArrowLeft /> {strings.table.pagination.buttonPrevious}
            </Button>
            <Button
              size="small"
              disabled={!paginationTokens.nextToken}
              style={styles.buttonNext}
              onClick={onNext}
            >
              <FiArrowRight /> {strings.table.pagination.buttonNext}
            </Button>
          </>
        )}
      </div>
      <ConfirmModal
        visible={modalDetails.visible}
        body={strings.formatString(
          modalDetails.type === "cancel"
            ? strings.modal.confirmCancelQuestion
            : strings.modal.confirmDeleteQuestion,
          {
            name: modalDetails.serviceOrderCode,
          }
        )}
        onCancel={() =>
          setModalDetails({ visible: false, serviceOrderId: null })
        }
        onConfirm={() =>
          modalDetails.type === "cancel"
            ? serviceOrderCancel(modalDetails.serviceOrderId)
            : serviceOrderDelete(modalDetails.serviceOrderId)
        }
      />
      {modalServiceOrder.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalServiceOrder.visible}
          onClose={() => setModalServiceOrder({ visible: false })}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalServiceOrder.action === "create" &&
                strings.modalServiceOrder.addTitle}
              {modalServiceOrder.action === "update" &&
                strings.modalServiceOrder.updateTitle}
              {modalServiceOrder.action === "execute" &&
                `${strings.modalServiceOrder.executeTitle}${
                  modalServiceOrder.offline ? " - 🔴Offline" : ""
                }`}
              {modalServiceOrder.action === "details" &&
                strings.modalServiceOrder.detailsTitle}
              {modalServiceOrder.action === "resume" &&
                strings.modalServiceOrder.resumeTitle}
              {modalServiceOrder.action === "read-photo" &&
                strings.modalServiceOrder.readPhotosTitle}
              {modalServiceOrder.action === "read-log" &&
                strings.modalServiceOrder.readLogsTitle}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              {modalServiceOrder.action === "create" && (
                <ServiceOrderCreateForm onSuccess={onSuccessServiceOrder} />
              )}
              {modalServiceOrder.action === "update" && (
                <ServiceOrderUpdateForm
                  serviceOrderId={modalServiceOrder.serviceOrderId}
                  onSuccess={onSuccessServiceOrder}
                />
              )}
              {modalServiceOrder.action === "execute" && (
                <ExecutedServiceOrderContainer
                  serviceOrderId={modalServiceOrder.serviceOrderId}
                  onSuccess={onSuccessServiceOrder}
                />
              )}
              {modalServiceOrder.action === "details" && (
                <ExecutedServiceOrderContainer
                  serviceOrderId={modalServiceOrder.serviceOrderId}
                  onSuccess={onSuccessServiceOrder}
                  viewType="export"
                />
              )}
              {modalServiceOrder.action === "resume" && (
                <ServiceOrderResume
                  serviceOrderId={modalServiceOrder.serviceOrderId}
                  onSuccess={onSuccessServiceOrder}
                />
              )}
              {modalServiceOrder.action === "read-photo" && (
                <ServiceOrderPhotos
                  serviceOrderID={modalServiceOrder.serviceOrderId}
                />
              )}
              {modalServiceOrder.action === "read-log" && (
                <ServiceOrderLogs
                  serviceOrderID={modalServiceOrder.serviceOrderId}
                />
              )}
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
      <CModal
        alignment="center"
        visible={modalDateVisible}
        onClose={() => setModalDateVisible(false)}
        portal={false}
        backdrop="static"
      >
        <CModalHeader>
          <CModalTitle>{strings.searchByDateRange}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <DateRangeForm
            initialValues={{
              from: searchBy.from && moment(searchBy.from).toDate(),
              to: searchBy.to && moment(searchBy.to).toDate(),
              options: {
                dateRangeStatus:
                  searchBy.dateRangeStatus || serviceOrderStatus.created,
              },
            }}
            onSubmit={onSubmitDateRange}
            withHours
            content={({ options, setOptions }) => (
              <>
                <div style={{ display: "flex", gap: 10 }}>
                  <SelectField
                    label={strings.status}
                    value={options?.dateRangeStatus || ""}
                    onChange={(e) =>
                      setOptions((o) => ({
                        ...o,
                        dateRangeStatus: e.target.value,
                      }))
                    }
                  >
                    {Object.entries(globalStrings.serviceOrderStatus).map(
                      (v) => (
                        <option key={v[0]} value={v[0]}>
                          {v[1] || "--"}
                        </option>
                      )
                    )}
                  </SelectField>
                </div>
              </>
            )}
          />
        </CModalBody>
      </CModal>
      {loading && <LoaderFullScreen />}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Órdenes de Servicio",
    status: "Estado",
    searchByDateRange: "Buscar por rango de fecha y estado",
    successExported: "Orden de Servicio exportada con éxito",
    successDeleted: "Orden de Servicio eliminada con éxito",
    successCanceled: "Orden de Servicio anulada con éxito",
    headers: {
      searchTechnician: "Buscar técnico",
    },
    serviceOrders: {
      addServiceOrder: "Agregar Orden de Servicio",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      execute: "Ejecutar Orden",
      export: "Exportar Orden",
      delete: "Eliminar",
      cancel: "Anular Orden",
      details: "Ver Detalles",
      readPhotos: "Ver Fotos",
      readLogs: "Ver Trazabilidad",
      moreOptions: "Más Opciones",
    },
    responses: {
      errorFetch: "Error al consultar orden de servicio",
      unexpectedError: "Ha ocurrido un error inesperado",
    },
    table: {
      headers: {
        name: "Nombre",
        leaderExecutor: "Líder Ejecutor",
        companyName: "Empresa",
        branchName: "Sede",
        orders: "Órdenes",
        actions: "Acciones",
      },
      pagination: {
        buttonPrevious: "Atrás",
        buttonNext: "Siguiente",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
        byState: "Buscar por estado",
      },
    },
    details: {
      title: "Detalles de la Orden de Servicio",
      description: "Descripción",
      billDate: "Fecha de facturación",
      billValue: "Valor de la factura",
      billCurrency: "Moneda de la factura",
      createdAt: "Fecha de creación",
    },
    modal: {
      confirmDeleteQuestion:
        "¿Estás seguro de eliminar la orden de servicio {name}?",
      confirmCancelQuestion:
        "¿Estás seguro de anular la orden de servicio {name}?",
      cancelButton: "Cancelar",
      confirmButton: "Confirmar",
    },
    modalServiceOrder: {
      addTitle: "Agregar Orden de Servicio",
      updateTitle: "Actualizar Orden de Servicio",
      executeTitle: "Ejecutar Orden de Servicio",
      detailsTitle: "Detalles de la Orden",
      resumeTitle: "Resumen de la Orden",
      readPhotosTitle: "Fotos de la Orden",
      readLogsTitle: "Trazabilidad de la Orden",
    },
  },
});

const styles = {
  buttonAdd: { float: "right" },
  modal: { width: "250px" },
  collapseCard: { marginTop: "15px" },
  buttonPrevious: { marginLeft: "15px" },
  buttonNext: { float: "right", marginRight: "15px" },
};

export default ServiceOrders;
